import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useGlobalToast } from '@/contexts/ToastContext';

interface PendingProvider {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  businessName: string;
  completionPercentage: number;
  verificationSubmittedAt: string;
}

export function ProviderVerificationPanel() {
  const [providers, setProviders] = useState<PendingProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const { success, error } = useGlobalToast();

  useEffect(() => {
    loadPendingVerifications();
  }, []);

  const loadPendingVerifications = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/admin/verifications/pending`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      error('Load Failed', 'Could not load pending verifications');
    } finally {
      setIsLoading(false);
    }
  };

  const viewDetails = async (providerId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/admin/verifications/provider?providerId=${providerId}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedProvider(data);
      }
    } catch (err) {
      error('Load Failed', 'Could not load provider details');
    }
  };

  const handleVerification = async (userId: string, action: 'approve' | 'reject') => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/admin/verifications/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            userId,
            action,
            reason: action === 'reject' ? rejectionReason : ''
          })
        }
      );

      if (response.ok) {
        success(
          action === 'approve' ? 'Provider Approved' : 'Provider Rejected',
          `Provider has been ${action}d successfully`
        );
        setSelectedProvider(null);
        setRejectionReason('');
        loadPendingVerifications();
      } else {
        error('Action Failed', 'Could not process verification');
      }
    } catch (err) {
      error('Action Failed', 'Could not process verification');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Provider Verifications</h2>
        <Badge variant="warning" className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          {providers.length} Pending
        </Badge>
      </div>

      {providers.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">No pending verifications</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {providers.map((provider) => (
            <Card key={provider.userId} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {provider.firstName} {provider.lastName}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{provider.email}</p>
                  <p className="text-sm text-gray-600 mb-2">
                    Business: {provider.businessName || 'Not specified'}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Completion: {provider.completionPercentage}%</span>
                    <span>
                      Submitted: {new Date(provider.verificationSubmittedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => viewDetails(provider.userId)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Review
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {selectedProvider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Provider Profile Review</h3>
            
            <div className="space-y-6 mb-8">
              <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                {JSON.stringify(selectedProvider.profile, null, 2)}
              </pre>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason (if rejecting)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Provide feedback for the provider..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleVerification(selectedProvider.profile.userId || '', 'approve')}
                  className="flex-1"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Provider
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleVerification(selectedProvider.profile.userId || '', 'reject')}
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  disabled={!rejectionReason}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedProvider(null);
                    setRejectionReason('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
